'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import { Volume2, VolumeX } from 'lucide-react';
import { useVideoLazyLoad } from '@/hooks/useVideoLazyLoad';

const Hero = () => {
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);
    useVideoLazyLoad(videoRef);

    const toggleAudio = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    return (
        <section className="bg-background pt-24 pb-0 overflow-hidden">
            <div className="container mx-auto px-5 md:px-10 max-w-[1280px]">
                {/* Content Top */}
                <div className="flex flex-col md:flex-row justify-between items-start gap-8 md:gap-12 mb-12 md:mb-20">
                    <div className="flex-1 max-w-[600px]">
                        {/* Tag */}
                        <div className="flex items-center gap-2 mb-4 md:mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                            <span className="text-[10px] md:text-[12px] font-semibold uppercase tracking-[1.5px] text-muted-foreground">
                                About Us
                            </span>
                        </div>

                        {/* Headline */}
                        <h2 className="text-foreground text-[36px] md:text-[48px] lg:text-[56px] leading-[1.1] md:leading-[1.15] font-medium tracking-[-0.02em]">
                            Smart Living. Made Simple.
                        </h2>
                    </div>

                    {/* Description Paragraph */}
                    <div className="md:self-center max-w-[650px] md:pt-8">
                        <p className="text-muted-foreground text-[15px] md:text-[16px] leading-[1.7] mb-4">
                            Hopo Homes is a tech-enabled co-living and rental management platform designed for modern urban living. We make it easier for tenants to find well-managed homes and for property owners to unlock better returns — without the usual stress of renting.
                        </p>
                        <p className="text-muted-foreground text-[15px] md:text-[16px] leading-[1.7]">
                            At Hopo, we believe renting should be simple, transparent, and hassle-free.
                        </p>
                    </div>
                </div>

                {/* Hero Visual */}
                <div className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden bg-black group">
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        aria-label={isMuted ? "Unmute video" : "Mute video"}
                        muted={isMuted}
                        playsInline
                        className="w-full h-full object-cover opacity-90 transition-opacity hover:opacity-100 duration-500"
                    >
                        <source src="/video/tenants&owner.mp4" type="video/mp4" />
                    </video>

                    {/* Audio Control */}
                    <button
                        onClick={toggleAudio}
                        className="absolute bottom-8 left-8 md:bottom-10 md:left-10 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all active:scale-95"
                    >
                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                </div>
            </div>
        </section>
    );
};

export default Hero;
